import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight, Plus, Users, X } from 'lucide-react';
import { Button } from '@qargo-ims/ui';
import { apiFetch } from '@/lib/api/client';
import { useApi } from '@/lib/api/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { titleCase } from '@/lib/format';
import { US_STATES } from '@/lib/usStates';

interface StoreRow {
  id: string;
  code: string;
  name: string;
  state: string | null;
  isKiosk: boolean;
  baristaCount: number;
  createdAt: string;
}
interface StoresResp {
  stores: StoreRow[];
}
interface RosterBarista {
  id: string;
  username: string;
  displayName: string | null;
  role: string;
  status: string;
  isHome: boolean;
  assigned: boolean;
}
interface RosterResp {
  storeId: string;
  storeCode: string;
  baristas: RosterBarista[];
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

const emptyForm = {
  code: '',
  name: '',
  address: '',
  state: '',
  isKiosk: false,
  phone: '',
  email: '',
  teamsWebhookUrl: '',
  notes: '',
};

export function AdminLocations() {
  const { data, loading, error, refetch } = useApi<StoresResp>('/stores');
  const stores = data?.stores ?? [];

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [rosterStore, setRosterStore] = useState<StoreRow | null>(null);
  const [stateFilter, setStateFilter] = useState('');

  const presentStates = useMemo(
    () => US_STATES.filter((s) => stores.some((st) => st.state === s.code)),
    [stores],
  );
  const visible = stateFilter ? stores.filter((s) => s.state === stateFilter) : stores;

  async function submitCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setFormErr(null);
    try {
      const trimmed = (v: string) => (v.trim() ? v.trim() : undefined);
      await apiFetch('/stores', {
        method: 'POST',
        body: JSON.stringify({
          code: form.code,
          name: form.name,
          address: trimmed(form.address),
          state: trimmed(form.state),
          isKiosk: form.isKiosk,
          phone: trimmed(form.phone),
          email: trimmed(form.email),
          teamsWebhookUrl: trimmed(form.teamsWebhookUrl),
          notes: trimmed(form.notes),
        }),
      });
      setForm(emptyForm);
      setShowCreate(false);
      refetch();
    } catch (err) {
      setFormErr(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }

  function field(
    label: string,
    key: Exclude<keyof typeof emptyForm, 'isKiosk'>,
    placeholder: string,
    max?: number,
  ) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <input
          className={inputClass}
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          placeholder={placeholder}
          maxLength={max}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Locations"
        description="Create stores, record their details, and assign baristas to the locations they work."
        actions={
          <Button onClick={() => setShowCreate((v) => !v)}>
            <Plus className="size-4" aria-hidden />
            New location
          </Button>
        }
      />

      {showCreate ? (
        <Card>
          <CardHeader>
            <CardTitle>New location</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitCreate} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Location Code</label>
                <input
                  className={inputClass}
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="M215"
                  maxLength={4}
                  pattern="[A-Za-z0-9]{1,4}"
                  title="Up to 4 alphanumeric characters — the store identifier"
                  required
                />
              </div>
              <div className="space-y-1 lg:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Name</label>
                <input
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Tampa — Downtown"
                  required
                />
              </div>
              {field('Address', 'address', '123 Main St', 256)}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">State</label>
                <select
                  className={inputClass}
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                >
                  <option value="">—</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code} — {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Kiosk</label>
                <label className="flex h-9 cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={form.isKiosk}
                    onChange={(e) => setForm({ ...form, isKiosk: e.target.checked })}
                  />
                  This location is a kiosk
                </label>
              </div>
              {field('Phone', 'phone', '(813) 555-0100', 32)}
              {field('Email', 'email', 'tpa@qargo.example', 256)}
              {field('Teams webhook URL', 'teamsWebhookUrl', 'https://…webhook…', 512)}
              <div className="space-y-1 sm:col-span-2 lg:col-span-3">
                <label className="text-xs font-medium text-muted-foreground">Notes</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything noteworthy about this location"
                />
              </div>
              {formErr ? (
                <p className="text-sm text-destructive sm:col-span-2 lg:col-span-3">{formErr}</p>
              ) : null}
              <div className="flex gap-2 sm:col-span-2 lg:col-span-3">
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating…' : 'Create location'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {presentStates.length > 0 ? (
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-muted-foreground">Filter by state</label>
          <select
            className="rounded-md border border-input bg-background px-2 py-1 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
          >
            <option value="">All states</option>
            {presentStates.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {error ? <p className="text-sm text-destructive">Could not load locations: {error}</p> : null}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={stateFilter ? 'No locations in this state' : 'No locations yet'}
          description={
            stateFilter
              ? 'Try a different state filter.'
              : 'Create your first store to start assigning baristas.'
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((s) => (
            <Card key={s.id} className="flex flex-col">
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <Badge variant="secondary">{s.code}</Badge>
                    {s.name}
                  </CardTitle>
                  <span className="flex items-center gap-1">
                    {s.isKiosk ? <Badge variant="outline">Kiosk</Badge> : null}
                    {s.state ? <Badge variant="muted">{s.state}</Badge> : null}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-3 pt-0">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Users className="size-3.5" aria-hidden />
                    {s.baristaCount} barista{s.baristaCount === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="mt-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRosterStore(s)}>
                    <Users className="size-4" aria-hidden />
                    Roster
                  </Button>
                  <Link
                    to={`/admin/locations/${s.id}`}
                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm font-medium text-primary transition-colors hover:underline"
                  >
                    Details
                    <ChevronRight className="size-4" aria-hidden />
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {rosterStore ? (
        <RosterEditor
          store={rosterStore}
          onClose={() => setRosterStore(null)}
          onSaved={() => {
            setRosterStore(null);
            refetch();
          }}
        />
      ) : null}
    </div>
  );
}

function RosterEditor({
  store,
  onClose,
  onSaved,
}: {
  store: StoreRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data, loading } = useApi<RosterResp>(`/stores/${store.id}/baristas`);
  const baristas = data?.baristas ?? [];
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const sel = selected ?? new Set(baristas.filter((b) => b.assigned).map((b) => b.id));

  function toggle(id: string) {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  async function save() {
    setSaving(true);
    setErrMsg(null);
    try {
      await apiFetch(`/stores/${store.id}/baristas`, {
        method: 'PUT',
        body: JSON.stringify({ baristaIds: [...sel] }),
      });
      onSaved();
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <Card
        className="flex max-h-[85vh] w-full max-w-lg flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Badge variant="secondary">{store.code}</Badge>
              Roster — {store.name}
            </CardTitle>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto pt-0">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : baristas.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No baristas in this org yet"
              description="Create baristas in Manage Users, then assign them here."
            />
          ) : (
            <ul className="divide-y divide-border">
              {baristas.map((b) => (
                <li key={b.id}>
                  <label className="flex cursor-pointer items-center gap-3 py-2.5">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      checked={sel.has(b.id)}
                      onChange={() => toggle(b.id)}
                    />
                    <span className="flex-1">
                      <span className="text-sm font-medium">{b.displayName ?? b.username}</span>
                      <span className="ml-2 text-xs text-muted-foreground">@{b.username}</span>
                    </span>
                    <Badge variant="muted">{titleCase(b.role)}</Badge>
                    {b.isHome ? <Badge variant="outline">Home</Badge> : null}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          {errMsg ? (
            <p className="text-sm text-destructive">{errMsg}</p>
          ) : (
            <p className="text-xs text-muted-foreground">{sel.size} assigned</p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save roster'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
